// ══════════════════════════════════════════════════════════════════
//  Conferência do boleto — o boleto em duas etapas
// ══════════════════════════════════════════════════════════════════
//  1. PREPARAR: a tela mostra planos e adicionais, o CS confere licenças,
//     valor do adicional e bonificação, e manda ao Hub (`cs.boleto.preparar`).
//     O Hub abre as vagas e devolve a mensalidade que ELE calcula.
//  2. GERAR: a tela compara o esperado com o calculado pelo Hub e só então
//     pede a cobrança (`cs.boleto.gerar`).
//
//  Por que separar: `provisionar-vidas` SOMA vagas e não tem remoção, e o Hub
//  não aceita valor — calcula. Conferir antes de abrir vaga e conferir de novo
//  antes de cobrar são as duas chances de pegar o erro sem boleto errado.
//
//  O valor dos PLANOS não se edita aqui: é o preço travado na venda ou, se a
//  venda não tem o plano, o do catálogo. Só o adicional muda (valor e
//  bonificação), que é onde a negociação acontece: o valor padrão é o do
//  catálogo, e o CS personaliza quando foi combinado outro.
// ══════════════════════════════════════════════════════════════════

import type { Plan } from '../plans/ports/IPlansRepository';
import type {
  AdicionalCodigo, ConferenciaEnviada, ContratoDocumento, HubDoPreparo, PlanoContratado, Vida,
} from './ports/ICsRepository';
import { chavePlano, contaComoVida, normalizeText, PISO_VIDAS_BOLETO_HUB } from './csModel';

// Os tipos do que vai e volta do Hub moram na porta (o repositório lê o
// preparo gravado); daqui só são reexportados.
export type { AdicionalCodigo, ConferenciaEnviada, HubDoPreparo, PreparoBoleto } from './ports/ICsRepository';

/** Meses de isenção padrão do adicional bonificado, contando o mês da assinatura. */
export const MESES_BONIFICACAO = 6;
/** Teto do campo de meses: bonificação mais longa que isso é erro de digitação. */
export const MESES_BONIFICACAO_MAX = 36;

/** O que a conferência lê do catálogo de planos: só nome, preço e tabela. */
export type ItemDoCatalogo = Pick<Plan, 'name' | 'unit_value'> & Partial<Pick<Plan, 'kind' | 'customer_type'>>;

/** O executor da rota Hub só cobra: não abre vagas nem confere o preparo.
 *  Mesma frase da recusa do servidor (server/cs-automation.js). */
export const BOLETO_SO_PELO_N8N =
  'O boleto em duas etapas ainda só sai pelo n8n. Volte a rota no Super Admin › Integrações.';

/** Diferença tolerada entre o esperado e o Hub: arredondamento. */
export const TOLERANCIA_BOLETO_CENTS = 1;

const ADICIONAIS: Array<{ codigo: AdicionalCodigo; match: RegExp; nome: string; campoVida: 'odonto' | 'psicologo' | null }> = [
  { codigo: 'clin_odonto', match: /odonto/,           nome: 'Plano odontológico', campoVida: 'odonto' },
  { codigo: 'zenklub_psi', match: /psicolog|zenklub/, nome: 'Psicólogo na tela',  campoVida: 'psicologo' },
  { codigo: 'totalpass',   match: /total\s*pass/,     nome: 'TotalPass',          campoVida: null },
];

export interface PlanoConferido {
  nome: string;
  /** Vidas cadastradas neste plano — é o que vira vaga no Hub. */
  quantidade: number;
  /** Quanto a venda previa. Só referência. */
  contratado: number;
  /** Da venda; sem o plano na venda, do catálogo. Entra no total, não na tela. */
  valorUnitarioCents: number;
  /** Nem a venda nem o catálogo têm o plano: o esperado sai errado. */
  semPreco: boolean;
}

export interface AdicionalConferido {
  codigo: AdicionalCodigo;
  nome: string;
  licencas: number;
  contratado: number;
  /** Vidas com a marcação (odonto/psicólogo). `null` no TotalPass, que não
   *  é marcado por vida. */
  marcadasNasVidas: number | null;
  /** O que vai ao Hub. Sem `personalizado`, é o `valorPadraoCents`. Com
   *  `bonificado`, não entra no total. */
  valorUnitarioCents: number;
  /** Preço do catálogo (sem o adicional no catálogo, o da venda). */
  valorPadraoCents: number;
  /** O CS (ou a venda negociada) fixou outro valor que não o padrão. */
  personalizado: boolean;
  /** O preço da venda, só referência. */
  valorVendaCents: number;
  bonificado: boolean;
  /** Meses sem cobrança, contando o mês da assinatura. */
  mesesBonificacao: number;
}

export interface ConferenciaBoleto {
  planos: PlanoConferido[];
  adicionais: AdicionalConferido[];
  /** Primeira assinatura do contrato (AAAA-MM-DD). Âncora da bonificação. */
  dataAssinatura: string | null;
}

// `chavePlano` com o "+" colado: "Vida +" da planilha e "Plano VIDA+" da
// venda são o mesmo plano.
const chaveDoPlano = (nome: unknown): string => chavePlano(nome, true).replace(/\s*\+\s*/g, '+');

// Tabela que vale no boleto, que é de empresa: corporate, depois preço
// único, por último varejo.
const PRIORIDADE_TABELA: Record<string, number> = { corporate: 0, ambos: 1, varejo: 2 };

const cents = (reais: number): number => Math.round((Number(reais) || 0) * 100);
const inteiro = (n: unknown): number => Math.max(0, Math.floor(Number(n) || 0));

/** AAAA-MM do último mês isento. 2026-10-08 + 6 meses → 2027-03 (outubro é o 1º). */
export const isentoAteDe = (dataAssinatura: string | null, meses = MESES_BONIFICACAO): string | null => {
  const m = /^(\d{4})-(\d{2})-\d{2}/.exec(String(dataAssinatura ?? ''));
  if (!m || meses < 1) return null;
  const ano = Number(m[1]); const mes = Number(m[2]);
  if (mes < 1 || mes > 12) return null;
  const ultimo = new Date(Date.UTC(ano, mes - 1 + meses - 1, 1));
  return ultimo.toISOString().slice(0, 7);
};

/** O inverso de `isentoAteDe`: quantos meses vão da assinatura ao último
 *  mês isento. Serve para reabrir o que foi enviado ao Hub. */
export const mesesAteIsento = (dataAssinatura: string | null, isentoAte: string | null): number | null => {
  const a = /^(\d{4})-(\d{2})/.exec(String(dataAssinatura ?? ''));
  const f = /^(\d{4})-(\d{2})$/.exec(String(isentoAte ?? ''));
  if (!a || !f) return null;
  const meses = (Number(f[1]) * 12 + Number(f[2])) - (Number(a[1]) * 12 + Number(a[2])) + 1;
  return meses >= 1 ? meses : null;
};

// `signedAt` é gravado em UTC: 31/10 às 22h em Brasília vira 01/11 e daria
// um mês de isenção a mais. A data que vale é a de Brasília.
const DIA_EM_BRASILIA = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit' });
const diaEmBrasilia = (valor: string): string | null => {
  if (/^\d{4}-\d{2}-\d{2}$/.test(valor)) return valor; // só data: já é o dia
  const d = new Date(valor);
  return Number.isNaN(d.getTime()) ? null : DIA_EM_BRASILIA.format(d);
};

const primeiraAssinatura = (documentos: ContratoDocumento[]): string | null => {
  const datas = documentos
    .filter(d => d.status === 'assinado' && d.signedAt)
    .map(d => diaEmBrasilia(String(d.signedAt)))
    .filter((d): d is string => !!d)
    .sort();
  return datas[0] ?? null;
};

/**
 * Ponto de partida da conferência: planos das vidas cadastradas, adicionais
 * da venda. Licenças de odonto e psicólogo começam pelas vidas marcadas —
 * é por elas que o Hub liga o adicional à vaga; sem marcação, pela venda.
 * Adicional vendido a R$ 0 começa bonificado.
 */
export const montarConferenciaBoleto = (p: {
  vidas: Vida[];
  planosContratados: PlanoContratado[];
  documentos: ContratoDocumento[];
  /** Catálogo de planos do workspace: preço padrão de adicional e de plano
   *  que a venda não tem. */
  catalogo?: ItemDoCatalogo[];
}): ConferenciaBoleto => {
  const vidasAtivas = p.vidas.filter(v => v.ativo !== false);
  const catalogo = [...(p.catalogo ?? [])]
    .sort((a, b) => (PRIORIDADE_TABELA[a.customer_type ?? 'ambos'] ?? 1) - (PRIORIDADE_TABELA[b.customer_type ?? 'ambos'] ?? 1));
  const precoNoCatalogo = new Map<string, number>();
  catalogo.forEach((item) => {
    const chave = chaveDoPlano(item.name);
    if (!precoNoCatalogo.has(chave)) precoNoCatalogo.set(chave, cents(item.unit_value));
  });
  // Adicional é linha `addon` que casa com odonto/psicólogo/TotalPass. Vida+
  // também é `addon` na venda, mas no Hub é plano.
  const adicionalDe = (l: PlanoContratado) => (l.adicional ? ADICIONAIS.find(a => a.match.test(normalizeText(l.nome))) : undefined);
  // Linhas repetidas na venda somam; o preço é o da primeira.
  const precoDaVenda = new Map<string, { quantidade: number; valorUnitario: number }>();
  p.planosContratados.filter(l => !adicionalDe(l)).forEach((l) => {
    const chave = chaveDoPlano(l.nome);
    const atual = precoDaVenda.get(chave);
    if (atual) atual.quantidade += inteiro(l.quantidade);
    else precoDaVenda.set(chave, { quantidade: inteiro(l.quantidade), valorUnitario: l.valorUnitario });
  });

  const porPlano = new Map<string, { nome: string; quantidade: number }>();
  vidasAtivas.filter(v => contaComoVida(v.tipo)).forEach((v) => {
    const chave = chaveDoPlano(v.planoNomeRaw);
    const atual = porPlano.get(chave) ?? { nome: v.planoNomeRaw.trim() || '(sem plano)', quantidade: 0 };
    atual.quantidade += 1;
    porPlano.set(chave, atual);
  });
  // Plano vendido sem nenhuma vida também aparece: zero cadastrado contra N
  // contratado é justamente o que o CS precisa ver. Adicional fica de fora.
  p.planosContratados
    .filter(l => !adicionalDe(l))
    .forEach((l) => {
      const chave = chaveDoPlano(l.nome);
      if (!porPlano.has(chave)) porPlano.set(chave, { nome: l.nome, quantidade: 0 });
    });

  const planos: PlanoConferido[] = [...porPlano].map(([chave, { nome, quantidade }]) => {
    const venda = precoDaVenda.get(chave);
    const doCatalogo = precoNoCatalogo.get(chave);
    return {
      nome,
      quantidade,
      contratado: venda ? venda.quantidade : 0,
      valorUnitarioCents: venda ? cents(venda.valorUnitario) : (doCatalogo ?? 0),
      semPreco: !venda && doCatalogo === undefined && quantidade > 0,
    };
  });

  const adicionais: AdicionalConferido[] = [];
  ADICIONAIS.forEach((a) => {
    const linhas = p.planosContratados.filter(l => adicionalDe(l) === a);
    const marcadas = a.campoVida ? vidasAtivas.filter(v => v[a.campoVida as 'odonto' | 'psicologo']).length : null;
    if (!linhas.length && !marcadas) return;
    const contratado = linhas.reduce((s, l) => s + inteiro(l.quantidade), 0);
    const venda = linhas[0];
    const valorVendaCents = venda ? cents(venda.valorUnitario) : 0;
    // Item de adicional primeiro: um plano com "odonto" no nome só dá preço
    // se o catálogo não tiver o adicional (há workspace com o odonto
    // cadastrado como plano). Entre dois que casam (o legado e o atual), o
    // de nome mais curto.
    const ehAddon = (item: ItemDoCatalogo) => (item.kind ?? 'addon') === 'addon';
    const doCatalogo = catalogo
      .filter(item => a.match.test(normalizeText(item.name)))
      .sort((x, y) => Number(ehAddon(y)) - Number(ehAddon(x)) || x.name.length - y.name.length)[0];
    const valorPadraoCents = doCatalogo ? cents(doCatalogo.unit_value) : valorVendaCents;
    // Nenhuma vida marcada não zera o que foi vendido: começa pela venda, e
    // o aviso de licenças × marcações aparece.
    const licencas = marcadas || contratado;
    const bonificado = !!venda && valorVendaCents === 0 && licencas > 0;
    // A venda que negociou outro preço (não zero, que é bonificação) já
    // começa personalizada com ele; senão vale o padrão do catálogo. Venda
    // que bateu com a tabela do dia segue o catálogo de hoje. Linha antiga,
    // sem tabela gravada, com preço diferente: conta como negociada — não
    // se troca o preço da venda em silêncio.
    const negociado = !!venda && !bonificado && valorVendaCents > 0 && valorVendaCents !== valorPadraoCents
      && (venda.valorTabela === null || cents(venda.valorTabela) !== valorVendaCents);
    adicionais.push({
      codigo: a.codigo,
      nome: a.nome,
      licencas,
      contratado,
      marcadasNasVidas: marcadas,
      valorUnitarioCents: negociado ? valorVendaCents : valorPadraoCents,
      valorPadraoCents,
      personalizado: negociado,
      valorVendaCents,
      bonificado,
      mesesBonificacao: bonificado && venda && inteiro(venda.meses) >= 1
        ? Math.min(MESES_BONIFICACAO_MAX, inteiro(venda.meses))
        : MESES_BONIFICACAO,
    });
  });

  return { planos, adicionais, dataAssinatura: primeiraAssinatura(p.documentos) };
};

export const totalVidasConferidas = (c: ConferenciaBoleto): number =>
  c.planos.reduce((s, pl) => s + pl.quantidade, 0);

export const subtotalAdicional = (a: AdicionalConferido): number =>
  a.bonificado ? 0 : a.licencas * a.valorUnitarioCents;

export const totalConferenciaCents = (c: ConferenciaBoleto): number =>
  c.planos.reduce((s, pl) => s + pl.quantidade * pl.valorUnitarioCents, 0)
  + c.adicionais.reduce((s, a) => s + subtotalAdicional(a), 0);

/** `bloqueios` impedem enviar ao Hub; `avisos` só informam. */
export const problemasDaConferencia = (
  c: ConferenciaBoleto,
  extra: {
    rascunhosPendentes?: number;
    /** AAAA-MM da competência a cobrar. Com ele, avisa bonificação vencida. */
    mesCompetencia?: string;
  } = {},
): { bloqueios: string[]; avisos: string[] } => {
  const bloqueios: string[] = [];
  const avisos: string[] = [];
  const vidas = totalVidasConferidas(c);

  if (!vidas) {
    bloqueios.push('Nenhuma vida cadastrada para cobrar. Importe as vidas antes: é por elas que o Hub abre as vagas.');
  } else if (vidas < PISO_VIDAS_BOLETO_HUB) {
    avisos.push(`Com ${vidas} vida(s) a empresa fica abaixo do mínimo de ${PISO_VIDAS_BOLETO_HUB} do BlueHub: o Hub não emite o boleto.`);
  }
  // Bloqueia: as vagas abririam (sem volta) com o esperado já errado, e a
  // etapa 2 nunca liberaria o boleto.
  c.planos.filter(pl => pl.semPreco).forEach((pl) => {
    bloqueios.push(`"${pl.nome}" não tem preço nem na venda nem no catálogo de planos: o valor esperado sai errado. Corrija o plano das vidas ou cadastre o plano.`);
  });
  c.planos.filter(pl => pl.contratado && pl.quantidade !== pl.contratado).forEach((pl) => {
    avisos.push(`${pl.nome}: ${pl.quantidade} cadastrada(s), a venda previa ${pl.contratado}.`);
  });
  c.adicionais.forEach((a) => {
    if (!Number.isInteger(a.licencas) || a.licencas < 0) bloqueios.push(`${a.nome}: licenças inválidas.`);
    if (!Number.isInteger(a.valorUnitarioCents) || a.valorUnitarioCents < 0) bloqueios.push(`${a.nome}: valor inválido.`);
    if (a.bonificado && (!Number.isInteger(a.mesesBonificacao) || a.mesesBonificacao < 1 || a.mesesBonificacao > MESES_BONIFICACAO_MAX)) {
      bloqueios.push(`${a.nome}: informe de 1 a ${MESES_BONIFICACAO_MAX} meses de bonificação.`);
    }
    const isentoAte = isentoAteDe(c.dataAssinatura, a.mesesBonificacao);
    if (a.marcadasNasVidas !== null && a.licencas !== a.marcadasNasVidas) {
      avisos.push(`${a.nome}: ${a.licencas} licença(s), mas ${a.marcadasNasVidas} vida(s) marcada(s). O Hub liga o adicional à vaga pela marcação.`);
    }
    if (a.bonificado && a.licencas > 0 && !c.dataAssinatura) {
      avisos.push(`${a.nome} bonificado sem contrato assinado: a isenção conta da assinatura, e ainda não há data.`);
    }
    if (a.bonificado && a.licencas > 0 && isentoAte && extra.mesCompetencia && extra.mesCompetencia > isentoAte) {
      avisos.push(`${a.nome}: a bonificação terminou em ${isentoAte}. Se o Hub já cobra, desmarque "Bonificado" para o valor bater.`);
    }
  });
  const pendentes = inteiro(extra.rascunhosPendentes);
  if (pendentes) {
    avisos.push(`Há ${pendentes} linha(s) da planilha em rascunho, fora deste boleto. Resolva antes se devem ser cobradas neste mês.`);
  }
  return { bloqueios, avisos };
};

export const conferenciaParaEnvio = (c: ConferenciaBoleto): ConferenciaEnviada => {
  return {
    planos: c.planos.filter(pl => pl.quantidade > 0)
      .map(pl => ({ nome: pl.nome, quantidade: pl.quantidade, valor_unitario_cents: pl.valorUnitarioCents })),
    adicionais: c.adicionais.filter(a => a.licencas > 0).map(a => ({
      codigo: a.codigo, nome: a.nome, licencas: a.licencas,
      valor_unitario_cents: a.bonificado ? 0 : a.valorUnitarioCents,
      bonificado: a.bonificado, isento_ate: a.bonificado ? isentoAteDe(c.dataAssinatura, a.mesesBonificacao) : null,
    })),
    data_assinatura: c.dataAssinatura,
    total_cents: totalConferenciaCents(c),
  };
};

// ── Etapa 2: o que o Hub calculou ────────────────────────────────

const numeroOuNull = (x: unknown): number | null =>
  x === null || x === undefined || x === '' || !Number.isFinite(Number(x)) ? null : Number(x);

/**
 * O roteador do n8n embrulha a saída em `{ ok, result }`, às vezes em array.
 * Espelha `hubDoPreparo` em server/cs-automation.js. O fluxo de preparo
 * devolve `hub_vagas` (com `por_plano`); `vidas_hub`/`resposta_vidas` são
 * formatos anteriores.
 */
export const hubDaResposta = (response: unknown): HubDoPreparo | null => {
  type Obj = Record<string, unknown>;
  const objeto = (v: unknown): Obj | null => {
    const o = Array.isArray(v) ? v[0] : v;
    return o && typeof o === 'object' ? o as Obj : null;
  };
  let x = objeto(response);
  for (let i = 0; i < 4 && x && !('flow_status' in x) && objeto(x.result); i += 1) x = objeto(x.result);
  if (!x) return null;
  const h = objeto(x.hub_vagas) || objeto(x.vidas_hub) || objeto(x.resposta_vidas);
  if (!h) return null;
  const mensalidadeCents = numeroOuNull(h.mensalidade_cents);
  const totalVagas = numeroOuNull(h.total_vagas);
  if (mensalidadeCents === null && totalVagas === null) return null;
  return { mensalidadeCents, totalVagas, estado: typeof h.estado === 'string' ? h.estado : null };
};

// ── Vencimento combinado (etapa 2) ───────────────────────────────
//  O Hub aceita `vencimento` só na 1ª cobrança, só boleto, de amanhã (data
//  de Brasília) até 30 dias. Data no mês da competência seguinte exige
//  antecipar aquele mês — que esta tela não oferece —, então o teto é também
//  o último dia do mês corrente. Vazio = o Hub decide.

const DIA_ISO = (d: Date): string => DIA_EM_BRASILIA.format(d);

export const janelaVencimento = (agora = new Date()): { min: string; max: string; cortePeloMes: boolean } => {
  const [a, m, d] = DIA_ISO(agora).split('-').map(Number);
  const dia = (n: number) => new Date(Date.UTC(a, m - 1, d + n)).toISOString().slice(0, 10);
  const fimDoMes = new Date(Date.UTC(a, m, 0)).toISOString().slice(0, 10);
  const em30 = dia(30);
  const cortePeloMes = fimDoMes < em30;
  return { min: dia(1), max: cortePeloMes ? fimDoMes : em30, cortePeloMes };
};

const dataBr = (iso: string) => iso.split('-').reverse().join('/');

/** Motivo da recusa, ou null. Vazio é válido (o Hub escolhe). */
export const problemaDoVencimento = (vencimento: string, agora = new Date()): string | null => {
  if (!vencimento) return null;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(vencimento)) return 'Data de vencimento inválida.';
  const { min, max, cortePeloMes } = janelaVencimento(agora);
  if (vencimento < min) return 'O vencimento tem de ser a partir de amanhã.';
  if (vencimento > max) {
    return cortePeloMes
      ? `O vencimento vai até ${dataBr(max)}: data no mês seguinte exige antecipar aquela competência.`
      : `O vencimento vai até ${dataBr(max)} (30 dias).`;
  }
  return null;
};

export const compararComHub =(esperadoCents: number, hub: HubDoPreparo | null): {
  bate: boolean; diferencaCents: number | null;
} => {
  if (!hub || hub.mensalidadeCents === null) return { bate: false, diferencaCents: null };
  const diferencaCents = esperadoCents - hub.mensalidadeCents;
  return { bate: Math.abs(diferencaCents) <= TOLERANCIA_BOLETO_CENTS, diferencaCents };
};
